/**
 * Old app addresses move to their clean address (docs/agent-clean-addresses-plan.md, PR 1).
 *
 * Shardul, 22 and 23 Oct 26: every old address keeps working for ever by
 * moving to the new one. /app/l/<uuid>[/<page>[/domain]] is in emails,
 * WhatsApps (the import template's button is /app/l/{{2}}), bell rows, cards,
 * bookmarks and home-screen apps, so on Fly's own hosts it is replaced by
 * /app/<slug>/<page>... with the query and the hash kept (a bare address opens
 * the home, as before).
 *
 * The old page renders exactly as today while the slug is looked for, so an
 * old address never stops working and never waits on the move:
 *   - the slug is already in this tab's map (an overview listed it): the
 *     address is replaced before the page paints;
 *   - else, once signed in, the overview this page reads anyway, then
 *     agent-action-respond app_address; when either answers, the address is
 *     replaced where the page stands;
 *   - signed out, offline, an error, a refusal, or the 400 a function not yet
 *     redeployed gives an unknown op: nothing moves, today's page stays.
 * On an agency's white-label host, or with MOVE_OLD_APP_ADDRESSES off, nothing
 * here runs.
 *
 * The move never remounts the page: both addresses render through the one
 * frame (AppSlugShell), so React keeps the shell and the page as they are
 * and only the address bar changes.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { isAgencyMode } from '@/utils/agencyRouting';
import { MOVE_OLD_APP_ADDRESSES, appSlugsAreFor, isLocationUuid, parseAppLocationPath, rememberAppSlug, rememberAppSlugs, slugAddressOf, slugForLocation } from '@/lib/appAddress';
import { fetchOverview, knownSessionUser } from '../appData';

const LOG = '[AppLocationLegacy]';

/** What app_address answered: the location, its current slug, and whether the slug asked for is that one. */
export interface AppAddressAnswer {
  location_id: string;
  slug: string;
  current: boolean;
}

export type AppAddressResult =
  | { kind: 'ok'; answer: AppAddressAnswer }
  /** 401, 403 or 404: the session cannot open it, or nothing is there (the server says the same either way). */
  | { kind: 'refused'; status: number }
  /** Offline, a server error, or an older function that knows no app_address (400). */
  | { kind: 'error'; status: number | null };

function statusOf(error: unknown): number | null {
  const status = (error as { context?: { status?: unknown } } | null)?.context?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * agent-action-respond op app_address, for the signed-in caller (the session bearer goes along).
 * `viewer` is that caller's user id: the answer is their own slug (PR 1b), remembered for them only.
 */
export async function askAppAddress(body: { slug: string } | { location_id: string }, viewer: string): Promise<AppAddressResult> {
  const { data, error } = await supabase.functions.invoke('agent-action-respond', { body: { op: 'app_address', ...body } });
  if (error) {
    const status = statusOf(error);
    if (status === 401 || status === 403 || status === 404) return { kind: 'refused', status };
    return { kind: 'error', status };
  }
  const answer = data as Partial<AppAddressAnswer> | null;
  if (!answer || !isLocationUuid(answer.location_id) || typeof answer.slug !== 'string') return { kind: 'error', status: null };
  rememberAppSlug(answer.location_id, answer.slug, viewer);
  return { kind: 'ok', answer: { location_id: answer.location_id, slug: answer.slug, current: answer.current === true } };
}

/**
 * A location app_address could not give a slug for is not asked again for a
 * while: an in-app link names the old shape while its slug is not known
 * (appPagePath), and until the functions are redeployed each tap would ask
 * once more and hear the same 400.
 */
const NO_SLUG_MS = 5 * 60_000;
const noSlugUntil = new Map<string, number>();

/** Test seam. */
export function resetSlugLookups(): void {
  noSlugUntil.clear();
}

/**
 * The slug of a location for the signed-in person, learned the cheap way
 * first: the map, then the overview (which fills the map), then app_address.
 * Null when it cannot be had; the old page simply stays.
 */
export async function learnSlugFor(locationId: string): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id ?? null;
  if (!userId) return null;
  // Slugs are per person (PR 1b): the map answers only while it holds this person's.
  const known = appSlugsAreFor(userId) ? slugForLocation(locationId) : null;
  if (known) return known;
  if ((noSlugUntil.get(locationId) ?? 0) > Date.now()) return null;
  const overview = await fetchOverview(false);
  if (overview.kind === 'ok' && overview.userId === userId) for (const ws of overview.value?.workspaces ?? []) rememberAppSlugs(ws?.locations, userId);
  const listed = appSlugsAreFor(userId) ? slugForLocation(locationId) : null;
  if (listed) return listed;
  const asked = await askAppAddress({ location_id: locationId }, userId);
  if (asked.kind !== 'ok') {
    noSlugUntil.set(locationId, Date.now() + NO_SLUG_MS);
    console.log(`${LOG} no slug for this location, the old address stays`, { location_id: locationId, kind: asked.kind, status: asked.status });
    return null;
  }
  return asked.answer.slug;
}

/** True when old addresses move on this host: Fly's own hosts, with the switch on. */
export function movesOldAddresses(): boolean {
  return MOVE_OLD_APP_ADDRESSES && !isAgencyMode();
}

/**
 * Moves the old address the page stands on to its clean address, once the
 * slug is known. `locationId` is the :locationId of an old route; undefined on
 * every other page, where it does nothing.
 */
export function useOldAddressMove(locationId: string | undefined): void {
  const location = useLocation();
  const navigate = useNavigate();
  const enabled = !!locationId && isLocationUuid(locationId) && movesOldAddresses();
  const [learned, setLearned] = useState<{ id: string; slug: string } | null>(null);
  // Only this person's own slugs (PR 1b): another account's slug for the same location would be the wrong name.
  const known = enabled && appSlugsAreFor(knownSessionUser()) ? slugForLocation(locationId) : null;
  const slug = known ?? (enabled && learned?.id === locationId ? learned.slug : null);
  const target = slug ? slugAddressOf(location.pathname, slug) : null;

  // The latest address, read when the move is made (the page may have changed its query meanwhile).
  const latest = useRef(location);
  latest.current = location;
  const moving = useRef<string | null>(null);

  // Before the page paints and before its own effects run, so nothing it does on mount is undone.
  useLayoutEffect(() => {
    if (!target) return;
    moving.current = target;
    const { search, hash } = latest.current;
    console.log(`${LOG} old address moved to its clean address`, { location_id: locationId });
    navigate({ pathname: target, search, hash }, { replace: true });
  }, [target, locationId, navigate]);

  // A router mounted in this same commit hears history only from its own layout effect, which runs
  // after this one, so a move made then would go unheard. Once the commit settles, if the address
  // is still the old one, the move is made again.
  useEffect(() => {
    if (!target) return undefined;
    const timer = setTimeout(() => {
      if (moving.current !== target || parseAppLocationPath(latest.current.pathname)?.shape !== 'id') return;
      const { search, hash } = latest.current;
      navigate({ pathname: target, search, hash }, { replace: true });
    }, 0);
    return () => clearTimeout(timer);
  }, [target, navigate]);

  useEffect(() => {
    if (!enabled || known || !locationId) return undefined;
    let alive = true;
    void learnSlugFor(locationId)
      .then((found) => {
        if (alive && found) setLearned({ id: locationId, slug: found });
      })
      .catch((err) => console.warn(`${LOG} could not look the slug up, the old address stays`, err));
    return () => {
      alive = false;
    };
  }, [enabled, known, locationId]);
}
